import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Image,
    SafeAreaView,
    Platform,
    ActivityIndicator,
    Alert,
    Linking
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getMySales, updateOrderStatus } from '../../api/marketplace';

const VendorOrderDetailScreen = ({ route, navigation }) => {
    const { orderId } = route.params;
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        fetchOrderDetails();
    }, [orderId]);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const data = await getMySales();

            // Re-group or find the specific order from sales list
            const grouped = data.reduce((acc, item) => {
                const oid = item.order_id;
                if (!acc[oid]) {
                    acc[oid] = {
                        ...item.order,
                        items: [],
                        total_sale_amount: 0
                    };
                }
                acc[oid].items.push(item);
                acc[oid].total_sale_amount += parseFloat(item.price_at_purchase) * item.quantity;
                return acc;
            }, {});

            setOrder(grouped[orderId]);
        } catch (error) {
            console.error('Fetch Vendor Order Error:', error);
            Alert.alert('Error', 'Unable to fetch order details');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        try {
            setUpdating(true);
            await updateOrderStatus(orderId, newStatus);
            Alert.alert("Success", `Order marked as ${newStatus.replace('_', ' ')}`);
            fetchOrderDetails(); // Refresh
        } catch (error) {
            Alert.alert("Update Failed", error || "Could not update order status");
        } finally {
            setUpdating(false);
        }
    };

    const callBuyer = () => {
        if (order?.contact_phone) {
            Linking.openURL(`tel:${order.contact_phone}`);
        }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#6366F1" />
            </View>
        );
    }

    if (!order) {
        return (
            <View style={styles.errorContainer}>
                <Text>Order not found</Text>
                <TouchableOpacity onPress={() => navigation.goBack()}>
                    <Text style={styles.backButtonText}>Go Back</Text>
                </TouchableOpacity>
            </View>
        );
    }

    const { status } = order;

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={24} color="#333" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Order Management</Text>
                <View style={{ width: 40 }} />
            </View>

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
                {/* Status Banner */}
                <View style={[
                    styles.statusBanner,
                    { backgroundColor: status === 'paid_processing' ? '#EEF2FF' : status === 'dispatched' ? '#E0F2FE' : '#ECFDF5' }
                ]}>
                    <Ionicons
                        name={status === 'paid_processing' ? 'time-outline' : status === 'dispatched' ? 'airplane-outline' : 'checkmark-circle-outline'}
                        size={20}
                        color={status === 'paid_processing' ? '#6366F1' : status === 'dispatched' ? '#0284C7' : '#10B981'}
                    />
                    <Text style={[
                        styles.statusBannerText,
                        { color: status === 'paid_processing' ? '#6366F1' : status === 'dispatched' ? '#0284C7' : '#10B981' }
                    ]}>
                        Status: {status.toUpperCase().replace('_', ' ')}
                    </Text>
                </View>

                {/* Buyer Card */}
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>Customer Information</Text>
                    <View style={styles.buyerDetails}>
                        <View style={styles.avatarCircle}>
                            <Text style={styles.avatarText}>{order.buyer?.name?.charAt(0) || 'B'}</Text>
                        </View>
                        <View style={styles.buyerInfo}>
                            <Text style={styles.buyerName}>{order.buyer?.name || 'Anonymous Buyer'}</Text>
                            <Text style={styles.buyerPhone}>{order.contact_phone || 'No phone provided'}</Text>
                        </View>
                        {order.contact_phone && (
                            <TouchableOpacity style={styles.callIcon} onPress={callBuyer}>
                                <Ionicons name="call" size={20} color="#6366F1" />
                            </TouchableOpacity>
                        )}
                    </View>

                    <View style={styles.separator} />

                    <Text style={styles.cardSubtitle}>Delivery Address</Text>
                    <View style={styles.addressContainer}>
                        <Ionicons name="location" size={18} color="#6366F1" style={{ marginTop: 2 }} />
                        <Text style={styles.addressText}>
                            {typeof order.delivery_address === 'object'
                                ? Object.values(order.delivery_address).join(', ')
                                : order.delivery_address || 'Standard Delivery'}
                        </Text>
                    </View>

                    {/* Map Snippet */}
                    <TouchableOpacity
                        style={styles.mapSnippet}
                        onPress={() => {
                            const addr = typeof order.delivery_address === 'object'
                                ? Object.values(order.delivery_address).join(', ')
                                : order.delivery_address;
                            Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`);
                        }}
                    >
                        <Image
                            source={{
                                uri: 'https://maps.googleapis.com/maps/api/staticmap?center=' +
                                    encodeURIComponent(typeof order.delivery_address === 'object' ? Object.values(order.delivery_address).join(', ') : order.delivery_address) +
                                    '&zoom=15&size=600x200&maptype=roadmap&markers=color:red%7Clabel:D%7C' +
                                    encodeURIComponent(typeof order.delivery_address === 'object' ? Object.values(order.delivery_address).join(', ') : order.delivery_address) +
                                    '&key='
                            }} // Key would go here, but using placeholder style for now
                            style={styles.mapImage}
                        />
                        <View style={styles.mapOverlay}>
                            <View style={styles.directionsBtn}>
                                <Ionicons name="navigate" size={16} color="#FFF" />
                                <Text style={styles.directionsText}>GET DIRECTIONS</Text>
                            </View>
                        </View>
                    </TouchableOpacity>
                </View>

                {/* Items Card */}
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>Items Ordered</Text>
                    <View style={styles.itemsList}>
                        {order.items.map((item, index) => (
                            <View key={index} style={[styles.itemRow, index !== order.items.length - 1 && styles.compactItemBorder]}>
                                <Image
                                    source={{ uri: item.item?.images?.[0] || 'https://via.placeholder.com/100' }}
                                    style={styles.itemImage}
                                />
                                <View style={styles.itemInfo}>
                                    <Text style={styles.itemName} numberOfLines={1}>{item.item?.name}</Text>
                                    <Text style={styles.itemDetailsText}>Qty: {item.quantity} • KES {Number(item.price_at_purchase).toLocaleString()}</Text>
                                </View>
                                <Text style={styles.rowPrice}>KES {(item.price_at_purchase * item.quantity).toLocaleString()}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.separator} />
                    <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>Total Sale Value</Text>
                        <Text style={styles.totalValue}>KES {order.total_sale_amount.toLocaleString()}</Text>
                    </View>
                </View>

                {/* Vendor Actions */}
                <View style={styles.actionsContainer}>
                    {status === 'paid_processing' && (
                        <TouchableOpacity
                            style={[styles.actionButton, styles.primaryAction]}
                            onPress={() => handleUpdateStatus('dispatched')}
                            disabled={updating}
                        >
                            {updating ? <ActivityIndicator color="#FFF" /> : (
                                <>
                                    <Ionicons name="airplane-outline" size={20} color="#FFF" />
                                    <Text style={styles.primaryActionText}>MARK AS DISPATCHED</Text>
                                </>
                            )}
                        </TouchableOpacity>
                    )}

                    {status === 'dispatched' && (
                        <TouchableOpacity
                            style={[styles.actionButton, styles.successAction]}
                            onPress={() => handleUpdateStatus('delivered')}
                            disabled={updating}
                        >
                            {updating ? <ActivityIndicator color="#FFF" /> : (
                                <>
                                    <Ionicons name="checkmark-circle-outline" size={20} color="#FFF" />
                                    <Text style={styles.successActionText}>MARK AS DELIVERED</Text>
                                </>
                            )}
                        </TouchableOpacity>
                    )}

                    {status === 'delivered' && (
                        <View style={styles.infoBox}>
                            <Ionicons name="information-circle-outline" size={20} color="#6B7280" />
                            <Text style={styles.infoText}>Awaiting buyer to confirm receipt and release funds.</Text>
                        </View>
                    )}

                    {status === 'completed' && (
                        <View style={[styles.infoBox, { backgroundColor: '#ECFDF5', borderColor: '#A7F3D0' }]}>
                            <Ionicons name="happy-outline" size={20} color="#059669" />
                            <Text style={[styles.infoText, { color: '#047857' }]}>Payment released to your wallet!</Text>
                        </View>
                    )}
                </View>

                <View style={{ height: 40 }} />
            </ScrollView>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: '#FFF',
        borderBottomWidth: 1,
        borderBottomColor: '#F3F4F6',
        paddingTop: Platform.OS === 'android' ? 40 : 12,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111',
    },
    backButton: {
        padding: 4,
    },
    scrollContent: {
        padding: 16,
    },
    statusBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 14,
        borderRadius: 12,
        marginBottom: 20,
        gap: 10,
    },
    statusBannerText: {
        fontSize: 14,
        fontWeight: '800',
        letterSpacing: 0.5,
    },
    card: {
        backgroundColor: '#FFF',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    cardTitle: {
        fontSize: 15,
        fontWeight: '800',
        color: '#111',
        marginBottom: 16,
    },
    cardSubtitle: {
        fontSize: 12,
        fontWeight: '700',
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    buyerDetails: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    avatarCircle: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#6366F1',
    },
    buyerInfo: {
        flex: 1,
        marginLeft: 12,
    },
    buyerName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111',
    },
    buyerPhone: {
        fontSize: 13,
        color: '#6B7280',
        marginTop: 2,
    },
    callIcon: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#EEF2FF',
        alignItems: 'center',
        justifyContent: 'center',
    },
    addressContainer: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: 16,
    },
    addressText: {
        flex: 1,
        fontSize: 14,
        color: '#4B5563',
        lineHeight: 20,
    },
    mapSnippet: {
        width: '100%',
        height: 120,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#F3F4F6',
        position: 'relative',
    },
    mapImage: {
        width: '100%',
        height: '100%',
        backgroundColor: '#E5E7EB',
    },
    mapOverlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0,0,0,0.1)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    directionsBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#6366F1',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 20,
        gap: 6,
    },
    directionsText: {
        color: '#FFF',
        fontSize: 11,
        fontWeight: '800',
    },
    itemsList: {
        marginTop: -8,
    },
    itemRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
    },
    compactItemBorder: {
        borderBottomWidth: 1,
        borderBottomColor: '#F9FAFB',
    },
    itemImage: {
        width: 44,
        height: 44,
        borderRadius: 8,
        backgroundColor: '#F9FAFB',
    },
    itemInfo: {
        flex: 1,
        marginLeft: 12,
    },
    itemName: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111',
    },
    itemDetailsText: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: 2,
    },
    rowPrice: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111',
    },
    separator: {
        height: 1,
        backgroundColor: '#F3F4F6',
        marginVertical: 16,
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalLabel: {
        fontSize: 14,
        color: '#6B7280',
    },
    totalValue: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111',
    },
    actionsContainer: {
        marginTop: 8,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 16,
        borderRadius: 16,
        gap: 10,
        ...Platform.select({
            ios: {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.1,
                shadowRadius: 10,
            },
            android: {
                elevation: 4,
            },
        }),
    },
    primaryAction: {
        backgroundColor: '#6366F1',
    },
    primaryActionText: {
        color: '#FFF',
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    successAction: {
        backgroundColor: '#10B981',
    },
    successActionText: {
        color: '#FFF',
        fontWeight: '900',
        letterSpacing: 0.5,
    },
    infoBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F3F4F6',
        padding: 16,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        gap: 10,
    },
    infoText: {
        flex: 1,
        fontSize: 13,
        color: '#6B7280',
        fontWeight: '500',
        lineHeight: 18,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    backButtonText: {
        color: '#6366F1',
        marginTop: 10,
        fontWeight: '600',
    }
});

export default VendorOrderDetailScreen;
